import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Container } from "reactstrap";
import { Check, EmailOutlined } from "@mui/icons-material";
import { AlertColor } from "@mui/material";
import withStyles from "isomorphic-style-loader/withStyles";
import * as yup from "yup";
import { useFormContext } from "react-hook-form";
import WizardFormStep from "../../components/WizardFormStep";
import FormInput from "../../../../../../toolkit/formInput";
import PrimaryButton from "../../../../../../toolkit/primaryButton";
import { savePaymentSetupData, StripeConfig } from "../../../../../../utils/firebase/paymentSetup";
import { sendStripeConnectOnboardingEmail, getStripeConnectOnboardingEmailStatus, issueStripeConnectOAuthState, StripeConnectOnboardingEmailStatusResponse } from "../../../../../../utils/stripeConnectOnboardingEmail";
import { createEmailField } from "../../../../../../utils/validation/validationUtils";
import { useLoading } from "../../../../../../hooks/useLoading";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import stripeLogo from "../../../../../../media/img/stripeLogo.png";
import OnboardingStepNotice from "../../components/OnboardingStepNotice";
import { formatOnboardingEmailDeliveryError } from "../../utils/formatOnboardingEmailDeliveryError";
import s from "./styles.scss";

export const stripeEmailInputSchema = yup.object({
  stripeInstructionEmail: createEmailField("Email address"),
});

interface StripeIntegrationStepProps {
  charityId?: string;
  stripeConfig?: StripeConfig | null;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  onExit?: () => void;
  onUseCustomUrl?: () => void;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
}

const StripeIntegrationStep: React.FC<StripeIntegrationStepProps> = ({
  charityId,
  stripeConfig,
  config,
  serviceAccessToken,
  onExit,
  onUseCustomUrl,
  showSnackbar,
}) => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailDeliveryStatus, setEmailDeliveryStatus] = useState<StripeConnectOnboardingEmailStatusResponse | null>(null);
  const { loading: isSendingEmail, runWithLoading } = useLoading();
  const { trigger, getValues, setValue, setError, clearErrors } = useFormContext();
  const emailStatusCharityRef = useRef<string | null>(null);
  const hasScheduledExitRef = useRef(false);

  const accountType = stripeConfig?.accountType || "";
  const stripeClientId = stripeConfig?.stripeClientId || "";
  const redirectUrl = stripeConfig?.redirectUrl || "";
  const isConnected = accountType === "connected";

  useEffect(() => {
    if (isConnected && charityId) {
      const saveStripeConnection = async () => {
        try {
          await savePaymentSetupData(charityId, {
            paymentMethod: "giveSpark",
            stripeConnected: true,
            progressTracker: "stripe_connected",
          });

          if (!hasScheduledExitRef.current && onExit) {
            hasScheduledExitRef.current = true;
            showSnackbar?.("Payment setup completed successfully!", "success");
            setTimeout(() => {
              onExit();
            }, 3000);
          }
        } catch (error) {
          console.error("Failed to save Stripe connection status:", error);
        }
      };

      saveStripeConnection();
    }
  }, [isConnected, charityId, onExit, showSnackbar]);

  useEffect(() => {
    if (!charityId || !config?.adminServiceUrl || !serviceAccessToken || isConnected) {
      return;
    }
    if (emailStatusCharityRef.current === charityId) {
      return;
    }

    emailStatusCharityRef.current = charityId;

    getStripeConnectOnboardingEmailStatus(config.adminServiceUrl, charityId, serviceAccessToken)
      .then(setEmailDeliveryStatus)
      .catch(() => {
        // banner is optional
      });
  }, [charityId, config?.adminServiceUrl, serviceAccessToken, isConnected]);

  const handleConnect = async () => {
    if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
      showSnackbar?.("Unable to connect to the admin service. Please refresh and try again.", "error");
      return;
    }
    const stripeWindow = window.open("", "_blank");
    try {
      const state = await issueStripeConnectOAuthState(config.adminServiceUrl, charityId, serviceAccessToken);
      const authorizeUrl = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${stripeClientId}&scope=read_write&state=${encodeURIComponent(state)}&redirect_uri=${redirectUrl}`;
      if (stripeWindow) {
        stripeWindow.location.href = authorizeUrl;
      } else {
        window.open(authorizeUrl, "_blank");
      }
    } catch (error) {
      stripeWindow?.close();
      showSnackbar?.("Unable to start the Stripe connection. Please try again.", "error");
    }
  };

  const handleOpenEmailForm = () => {
    setValue("stripeInstructionEmail", "");
    clearErrors("stripeInstructionEmail");
    setShowEmailForm(true);
  };

  const handleBackToStripeOptions = () => {
    setShowEmailForm(false);
    setValue("stripeInstructionEmail", "");
    clearErrors("stripeInstructionEmail");
  };

  const handleSendInstructionsEmail = async () => {
    clearErrors("stripeInstructionEmail");

    const isValid = await trigger("stripeInstructionEmail");
    if (!isValid) {
      return;
    }

    const adminEmail = getValues("stripeInstructionEmail")?.trim();

    if (!charityId || !stripeClientId || !redirectUrl) {
      const message =
        "Stripe configuration is incomplete. Please refresh and try again.";
      setError("stripeInstructionEmail", { message });
      showSnackbar?.(message, "error");
      return;
    }

    if (!config?.adminServiceUrl || !serviceAccessToken) {
      const message =
        "Unable to connect to the admin service. Please refresh and try again.";
      setError("stripeInstructionEmail", { message });
      showSnackbar?.(message, "error");
      return;
    }

    await runWithLoading(async () => {
      try {
        await sendStripeConnectOnboardingEmail(
          config.adminServiceUrl,
          charityId,
          serviceAccessToken,
          {
            redirectUrl,
            stripeClientId,
            adminEmail,
          }
        );
        setEmailDeliveryStatus({
          lastSentEmail: adminEmail,
          lastSentAt: Date.now(),
          deliveryFailed: false,
        });
        showSnackbar?.("Instructions email sent successfully.", "success");
        setValue("stripeInstructionEmail", "");
        setShowEmailForm(false);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to send email.";
        setError("stripeInstructionEmail", { message });
        showSnackbar?.(message, "error");
      }
    });
  };

  const renderOptionCard = (
    leading: React.ReactNode,
    title: string,
    subtitle: string,
    buttonLabel: string,
    onButtonClick: () => void,
    buttonDisabled?: boolean,
    buttonLoading?: boolean
  ) => (
    <div className={`${s.connectStripeContainer} ${s.optionCard}`}>
      <div className={s.leadingArea}>
        {leading}
      </div>
      <div className={s.textCol}>
        <div className={`d-flex flex-column ${s.textContainer}`}>
          <span className={s.elementTitle}>{title}</span>
          <span className={s.elementSubTitle}>{subtitle}</span>
        </div>
      </div>
      <div className={s.actionCol}>
        <PrimaryButton
          onClick={onButtonClick}
          variant="primary"
          className={s.actionButton}
          disabled={buttonDisabled}
          loading={buttonLoading}
          loadingText="Sending..."
        >
          {buttonLabel}
        </PrimaryButton>
      </div>
    </div>
  );

  const stepTitle = isConnected
    ? "Payment Setup Complete"
    : showEmailForm
      ? "Email instructions"
      : "Connect to Stripe";

  const stepSubtitle = isConnected
    ? "Your organization is ready to accept donations"
    : showEmailForm
      ? "Send connection steps through email"
      : "Connect your Stripe account to start accepting donations securely";

  return (
    <WizardFormStep title={stepTitle} subtitle={stepSubtitle}>
      <Container
        fluid
        className={`h-100 ${s.baseContainer}`}
        style={{ paddingBottom: "5%" }}
      >
        {isConnected ? (
          <Row className="mt-1 justify-content-center">
            <Col md="7">
              <div className={`${s.connectStripeContainer} ${s.optionCard} ${s.optionCardConnected}`}>
                <div className={s.leadingArea}>
                  <img src={stripeLogo} alt="Stripe logo" style={{ height: 40 }} />
                </div>
                <div className={s.textCol}>
                  <div className={`d-flex flex-column ${s.textContainer}`}>
                    <span className={s.elementTitle}>
                      <Check className={s.checkIcon} />
                      Connected to Stripe
                    </span>
                    <span className={s.elementSubTitle}>
                      Payments will be processed through your Stripe account
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        ) : showEmailForm ? (
          <>
            <Row className="mt-4 justify-content-center">
              <Col md="8">
                <FormInput
                  name="stripeInstructionEmail"
                  type="email"
                  icon="fa-solid fa-envelope"
                  placeholder="colleague@organization.org"
                />
              </Col>
            </Row>

            <Row className="justify-content-center mt-4 mb-4">
              <Col md={8} className="d-flex justify-content-center">
                <PrimaryButton
                  onClick={handleSendInstructionsEmail}
                  loading={isSendingEmail}
                  loadingText="Sending..."
                  loadingAnimation="pulse"
                  variant="primary"
                >
                  Send email
                </PrimaryButton>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} className="text-center">
                <span className={s.alternativeText}>
                  Prefer to connect Stripe yourself?{" "}
                  <button
                    type="button"
                    className={s.alternativeLink}
                    onClick={handleBackToStripeOptions}
                  >
                    Back to connection options
                  </button>
                </span>
              </Col>
            </Row>
          </>
        ) : (
          <>
            {emailDeliveryStatus?.deliveryFailed && emailDeliveryStatus.lastSentEmail && (
              <Row className="justify-content-center mb-4">
                <Col md="7">
                  <OnboardingStepNotice variant="error" requiresAttention>
                    {formatOnboardingEmailDeliveryError({
                      purpose: "Stripe setup",
                      email: emailDeliveryStatus.lastSentEmail,
                      timestamp: emailDeliveryStatus.lastSentAt,
                      reason:
                        emailDeliveryStatus.deliveryError || emailDeliveryStatus.deliveryStatus,
                    })}
                  </OnboardingStepNotice>
                </Col>
              </Row>
            )}

            <Row className="mt-1 justify-content-center">
            <Col md="7" className={s.optionsStack}>
              {renderOptionCard(
                <img src={stripeLogo} alt="Stripe logo" style={{ height: 40 }} />,
                "Have a Stripe account?",
                "On the next screen, log into your Stripe account.",
                "Connect",
                handleConnect
              )}
              {renderOptionCard(
                <EmailOutlined className={s.emailIcon} />,
                "Email instructions",
                "Send connection steps through email",
                "Send instructions",
                handleOpenEmailForm
              )}
            </Col>
          </Row>
          </>
        )}

        {!isConnected && !showEmailForm && onUseCustomUrl && (
          <Row className="mt-5 justify-content-center">
            <Col md="7" className="text-center">
              <span className={s.alternativeText}>
                Already have a donation platform?{" "}
                <button
                  type="button"
                  className={s.alternativeLink}
                  onClick={onUseCustomUrl}
                >
                  Use a custom URL instead
                </button>
              </span>
            </Col>
          </Row>
        )}
      </Container>
    </WizardFormStep>
  );
};

export default withStyles(s)(StripeIntegrationStep);
